import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { timelineApi } from "../lib/api";
import api from "../lib/api";
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Calendar,
  Save,
  X,
  ChevronUp,
  ChevronDown,
  Check,
  Settings2,
  Layers
} from "lucide-react";
import { cn } from "../lib/utils";
import TaskTimeline from "../components/TaskTimeline";

// Predefined colors for stages
const STAGE_COLORS = [
  { name: "Indigo", value: "#6366f1" },
  { name: "Orange", value: "#f97316" },
  { name: "Green", value: "#22c55e" },
  { name: "Blue", value: "#3b82f6" },
  { name: "Purple", value: "#a855f7" },
  { name: "Pink", value: "#ec4899" },
  { name: "Teal", value: "#14b8a6" },
  { name: "Yellow", value: "#eab308" },
  { name: "Red", value: "#ef4444" },
  { name: "Cyan", value: "#06b6d4" },
];

const TimelineSettingsPage = () => {
  const [templates, setTemplates] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Selected service type for editing
  const [selectedServiceType, setSelectedServiceType] = useState(null);
  const [editingStages, setEditingStages] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [templatesRes, typesRes] = await Promise.all([
        timelineApi.getTemplates(),
        api.get("/service-types/list")
      ]);
      setTemplates(templatesRes.data || []);
      
      // Convert service types to objects with value and label
      const types = typesRes.data || [];
      const typesWithLabels = types.map(t => ({
        value: t.name,
        label: t.name
      }));
      setServiceTypes(typesWithLabels);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load timeline templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get template for a service type
  const getTemplateForService = (serviceTypeValue) => {
    return templates.find(t => t.service_type === serviceTypeValue);
  };

  // Select a service type for editing
  const selectServiceType = (serviceType) => {
    if (hasChanges) {
      if (!window.confirm("You have unsaved changes. Discard them?")) {
        return;
      }
    }
    
    setSelectedServiceType(serviceType);
    const existing = getTemplateForService(serviceType.value);
    if (existing) {
      setEditingStages(existing.stages || []);
    } else {
      // Default stages for new template
      setEditingStages([
        { id: `s_${Date.now()}_1`, name: "Start", order: 0, color: "#6366f1" },
        { id: `s_${Date.now()}_2`, name: "In Progress", order: 1, color: "#f97316" },
        { id: `s_${Date.now()}_3`, name: "Complete", order: 2, color: "#22c55e" },
      ]);
    }
    setHasChanges(false);
  };

  // Stage management
  const addStage = () => {
    const newStage = {
      id: `s_${Date.now()}`,
      name: "",
      order: editingStages.length,
      color: STAGE_COLORS[editingStages.length % STAGE_COLORS.length].value
    };
    setEditingStages(prev => [...prev, newStage]);
    setHasChanges(true);
  };

  const removeStage = (index) => {
    setEditingStages(prev => 
      prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i }))
    );
    setHasChanges(true);
  };

  const updateStage = (index, field, value) => {
    setEditingStages(prev => 
      prev.map((s, i) => i === index ? { ...s, [field]: value } : s)
    );
    setHasChanges(true);
  };

  const moveStage = (index, direction) => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= editingStages.length) return;
    
    const newStages = [...editingStages];
    [newStages[index], newStages[newIndex]] = [newStages[newIndex], newStages[index]];
    newStages.forEach((s, i) => s.order = i);
    
    setEditingStages(newStages);
    setHasChanges(true);
  };

  // Save template
  const handleSave = async () => {
    if (!selectedServiceType) return;
    
    if (editingStages.length === 0) {
      toast.error("At least one stage is required");
      return;
    }
    
    if (editingStages.some(s => !s.name.trim())) {
      toast.error("All stages must have a name");
      return;
    }

    try {
      setSaving(true);
      const existing = getTemplateForService(selectedServiceType.value);
      
      if (existing) {
        // Update existing
        await timelineApi.updateTemplate(existing.id, {
          stages: editingStages,
          is_active: true
        });
        toast.success("Timeline updated");
      } else {
        // Create new
        await timelineApi.createTemplate({
          name: `${selectedServiceType.label} Timeline`,
          description: `Default timeline for ${selectedServiceType.label}`,
          service_type: selectedServiceType.value,
          stages: editingStages,
          is_active: true
        });
        toast.success("Timeline created");
      }
      
      setHasChanges(false);
      fetchData();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save timeline");
    } finally {
      setSaving(false);
    }
  };

  // Delete template for service
  const handleDelete = async () => {
    if (!selectedServiceType) return;
    
    const existing = getTemplateForService(selectedServiceType.value);
    if (!existing) return;
    
    if (!window.confirm(`Delete timeline for "${selectedServiceType.label}"?`)) {
      return;
    }
    
    try {
      await timelineApi.deleteTemplate(existing.id);
      toast.success("Timeline deleted");
      setEditingStages([]);
      setSelectedServiceType(null);
      setHasChanges(false);
      fetchData();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete timeline");
    }
  };

  return (
    <div className="p-6" data-testid="timeline-settings-page">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <Calendar className="h-6 w-6 text-orange-400" />
            Timeline Templates
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            The stages each service type's tasks move through — the only progress a task has. The last stage means done.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
        </div>
      ) : (
        <div className="flex gap-6">
          {/* Left Panel - Service Types List */}
          <div className="w-80 flex-shrink-0">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-3">
                <CardTitle className="text-zinc-50 text-sm flex items-center gap-2">
                  <Layers className="h-4 w-4 text-orange-400" />
                  Service Types
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-zinc-800">
                  {serviceTypes.map(serviceType => {
                    const template = getTemplateForService(serviceType.value);
                    const isSelected = selectedServiceType?.value === serviceType.value;
                    
                    return (
                      <button
                        key={serviceType.value}
                        onClick={() => selectServiceType(serviceType)}
                        className={cn(
                          "w-full p-4 text-left transition-colors flex items-center justify-between",
                          isSelected 
                            ? "bg-orange-500/10 border-l-2 border-orange-500" 
                            : "hover:bg-zinc-800/50 border-l-2 border-transparent"
                        )}
                      >
                        <div>
                          <p className={cn(
                            "font-medium text-sm",
                            isSelected ? "text-orange-400" : "text-zinc-50"
                          )}>
                            {serviceType.label}
                          </p>
                          {template ? (
                            <p className="text-[10px] text-zinc-500 mt-0.5">
                              {template.stages?.length || 0} stages configured
                            </p>
                          ) : (
                            <p className="text-[10px] text-zinc-600 mt-0.5">
                              No timeline configured
                            </p>
                          )}
                        </div>
                        
                        {template ? (
                          <Badge variant="outline" className="text-[10px] bg-green-500/10 text-green-400 border-green-500/30">
                            <Check className="w-2.5 h-2.5 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-zinc-800 text-zinc-500 border-zinc-700">
                            None
                          </Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Timeline Editor */}
          <div className="flex-1">
            {selectedServiceType ? (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-zinc-50 text-base flex items-center gap-2">
                      <Settings2 className="h-4 w-4 text-orange-400" />
                      {selectedServiceType.label} Timeline
                    </CardTitle>
                    <p className="text-xs text-zinc-500 mt-1">
                      Define the stages tasks will progress through
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {getTemplateForService(selectedServiceType.value) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDelete}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 text-xs"
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        Delete
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={handleSave}
                      disabled={saving || !hasChanges}
                      className="bg-orange-500 hover:bg-orange-600 h-8 text-xs"
                    >
                      <Save className="h-3.5 w-3.5 mr-1" />
                      {saving ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Stages List */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-zinc-300 text-sm">Stages</Label>
                        <p className="text-[11px] text-zinc-500 mt-0.5">
                          The PM moves a task through these stages in order. The last stage means the task is done.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addStage}
                        className="h-7 text-xs border-zinc-700 text-zinc-300 shrink-0"
                      >
                        <Plus className="h-3 w-3 mr-1" />Add Stage
                      </Button>
                    </div>

                    {editingStages.length === 0 ? (
                      <div className="py-8 text-center border border-dashed border-zinc-700 rounded-lg">
                        <p className="text-zinc-500 text-sm mb-2">No stages defined</p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addStage}
                          className="h-7 text-xs border-zinc-700 text-zinc-300"
                        >
                          <Plus className="h-3 w-3 mr-1" />Add First Stage
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {editingStages.map((stage, index) => (
                          <div
                            key={stage.id}
                            className="flex flex-wrap items-center gap-2 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50"
                          >
                            {/* Reorder buttons */}
                            <div className="flex flex-col gap-0.5">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => moveStage(index, "up")}
                                disabled={index === 0}
                                className="h-5 w-5 p-0 text-zinc-500 hover:text-zinc-50 disabled:opacity-30"
                              >
                                <ChevronUp className="h-3 w-3" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => moveStage(index, "down")}
                                disabled={index === editingStages.length - 1}
                                className="h-5 w-5 p-0 text-zinc-500 hover:text-zinc-50 disabled:opacity-30"
                              >
                                <ChevronDown className="h-3 w-3" />
                              </Button>
                            </div>

                            {/* Order number with color */}
                            <div 
                              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-zinc-50 flex-shrink-0"
                              style={{ backgroundColor: stage.color }}
                            >
                              {index + 1}
                            </div>

                            {/* Stage name */}
                            <Input
                              value={stage.name}
                              onChange={(e) => updateStage(index, "name", e.target.value)}
                              placeholder="Stage name"
                              className="flex-1 min-w-[160px] bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm"
                            />

                            {/* Color picker */}
                            <div className="flex items-center gap-1">
                              {STAGE_COLORS.slice(0, 6).map(color => (
                                <button
                                  key={color.value}
                                  type="button"
                                  onClick={() => updateStage(index, "color", color.value)}
                                  className={cn(
                                    "w-5 h-5 rounded-full transition-transform",
                                    stage.color === color.value 
                                      ? "ring-2 ring-white ring-offset-1 ring-offset-zinc-900 scale-110" 
                                      : "hover:scale-110"
                                  )}
                                  style={{ backgroundColor: color.value }}
                                  title={color.name}
                                />
                              ))}
                            </div>

                            {/* Remove button */}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeStage(index)}
                              className="h-8 w-8 p-0 text-zinc-500 hover:text-red-400"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Preview */}
                  {editingStages.length > 0 && (
                    <div className="pt-4 border-t border-zinc-800">
                      <Label className="text-zinc-400 text-xs mb-3 block">Preview</Label>
                      <div className="p-4 bg-zinc-800/30 rounded-lg">
                        <TaskTimeline 
                          stages={editingStages}
                          currentStageIndex={Math.floor(editingStages.length / 2)}
                          size="default"
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-16 text-center">
                  <Calendar className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-zinc-50 mb-2">Select a Service Type</h3>
                  <p className="text-zinc-400 text-sm">
                    Choose a service type from the list to configure its timeline stages.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimelineSettingsPage;
